/**
 * Request timeouts. A request the server accepts but never answers would otherwise leave the viewer
 * loading forever with no message. Every image and metadata request therefore carries an IDLE
 * timeout: it is aborted only when no bytes arrive for that long, so a large mammogram on a slow
 * site link keeps loading as long as data keeps flowing.
 */

/** Default idle timeout; `requestIdleTimeoutSeconds` in viewer_config.js overrides it. */
export const DEFAULT_REQUEST_IDLE_TIMEOUT_MS = 30_000;
const MIN_REQUEST_IDLE_TIMEOUT_MS = 1_000;
/** Statuses a Response cannot be constructed with a body for. */
const NULL_BODY_STATUSES = new Set([101, 204, 205, 304]);

let requestIdleTimeoutMs = DEFAULT_REQUEST_IDLE_TIMEOUT_MS;

export function setRequestIdleTimeout(seconds: number | undefined): void {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds)) return;
  requestIdleTimeoutMs = Math.max(MIN_REQUEST_IDLE_TIMEOUT_MS, Math.round(seconds * 1000));
}

export function requestIdleTimeout(): number {
  return requestIdleTimeoutMs;
}

export class RequestTimeoutError extends Error {
  constructor(readonly url: string) {
    super(`no data for ${Math.round(requestIdleTimeoutMs / 1000)} s from ${url.split('?')[0].slice(-80)}`);
    this.name = 'RequestTimeoutError';
  }
}

/**
 * True for a request that never produced an HTTP answer: idle timeout, aborted or dropped connection.
 * The WADO loader rejects with the XMLHttpRequest itself (status 0 when nothing came back); fetch
 * rejects with a TypeError, or with RequestTimeoutError from `timedFetch`.
 */
export function isNetworkFailure(error: unknown): boolean {
  if (error instanceof RequestTimeoutError || error instanceof TypeError) return true;
  if (typeof XMLHttpRequest !== 'undefined' && error instanceof XMLHttpRequest) return error.status === 0;
  return typeof error === 'object' && error !== null && 'status' in error && 'readyState' in error
    && (error as { status: unknown }).status === 0;
}

function urlOf(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  return input instanceof URL ? input.href : input.url;
}

/**
 * `fetch` with the idle timeout on both the wait for headers and the body. An `init.signal` from the
 * caller still aborts it. Rejects with RequestTimeoutError when the server goes quiet.
 */
export async function timedFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const url = urlOf(input);
  const controller = new AbortController();
  const outer = init.signal;
  if (outer) {
    if (outer.aborted) controller.abort(outer.reason);
    else outer.addEventListener('abort', () => controller.abort(outer.reason), { once: true });
  }
  let timer: ReturnType<typeof setTimeout> | undefined;
  const disarm = () => clearTimeout(timer);
  const arm = () => {
    disarm();
    timer = setTimeout(() => controller.abort(new RequestTimeoutError(url)), requestIdleTimeoutMs);
  };

  arm();
  let response: Response;
  try {
    response = await fetch(input, { ...init, signal: controller.signal });
  } catch (error) {
    disarm();
    throw controller.signal.reason instanceof RequestTimeoutError ? controller.signal.reason : error;
  }
  if (!response.body || NULL_BODY_STATUSES.has(response.status)) {
    disarm();
    return response;
  }

  const reader = response.body.getReader();
  arm();
  const body = new ReadableStream<Uint8Array>({
    async pull(stream) {
      try {
        const { done, value } = await reader.read();
        if (done) {
          disarm();
          stream.close();
          return;
        }
        arm();
        stream.enqueue(value);
      } catch (error) {
        disarm();
        stream.error(controller.signal.reason instanceof RequestTimeoutError ? controller.signal.reason : error);
      }
    },
    cancel(reason) {
      disarm();
      return reader.cancel(reason);
    },
  });
  return new Response(body, { status: response.status, statusText: response.statusText, headers: response.headers });
}

/**
 * Idle watchdog for an XMLHttpRequest (the WADO image loader's requests, from its `beforeSend`):
 * aborts it when no progress event arrives for the idle timeout. The loader treats the abort as a
 * failed load (status 0), which `isNetworkFailure` recognises.
 */
export function watchXhrIdle(xhr: XMLHttpRequest): void {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const disarm = () => clearTimeout(timer);
  const arm = () => {
    disarm();
    timer = setTimeout(() => {
      console.warn('[viewer] request idle timeout, aborting', xhr.responseURL || '(pending)');
      xhr.abort();
    }, requestIdleTimeoutMs);
  };
  xhr.addEventListener('loadstart', arm);
  xhr.addEventListener('progress', arm);
  xhr.addEventListener('loadend', disarm);
  arm();
}

const NOT_ACCEPTABLE = 406;

/**
 * A PACS-rendered image, one media type per request: Orthanc answers 400 to an Accept that lists two,
 * and an archive that renders lossless only (ours) answers 406 to JPEG, so JPEG is asked first and PNG
 * again on a 406. `headers` carries the auth; the Accept is set here.
 */
export async function fetchRendered(url: string, headers: Record<string, string>): Promise<Response> {
  let response = await timedFetch(url, { headers: { ...headers, Accept: 'image/jpeg' }, credentials: 'same-origin' });
  if (response.status === NOT_ACCEPTABLE) {
    response = await timedFetch(url, { headers: { ...headers, Accept: 'image/png' }, credentials: 'same-origin' });
  }
  return response;
}
